import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO, isToday, isTomorrow, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import { Users, FolderKanban, Eye, CalendarCheck, ChevronRight, Link2, Zap, Inbox, BarChart3, Bell, Plus, Archive, ArchiveRestore, Trash2, FileText, FileImage, FileSpreadsheet, File } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { frDate, fmtEuro, clientRequestStatus, pubVisualFull } from "@/lib/constants";
import BookingLinkModal from "@/components/BookingLinkModal";
import { NotifyClientButton } from "@/components/NotifyClientButton";
import { toast } from "sonner";

/* Pastels fonctionnels du Design System DGA */
const PASTEL = {
  blue: { bg: "#E0F2FE", text: "#0369A1", border: "#BAE6FD" },
  amber: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  purple: { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
  rose: { bg: "#FFE4E6", text: "#BE123C", border: "#FECDD3" },
  green: { bg: "#DCFCE7", text: "#15803D", border: "#BBF7D0" },
};
const SERVICE_BADGE = {
  cm: { label: "Community", ...PASTEL.blue },
  graphisme: { label: "Graphisme", ...PASTEL.amber },
  web: { label: "Web Design", ...PASTEL.purple },
  client: { label: "Client", ...PASTEL.green },
};
const GRAPHISME_FINAL = ["paye", "livre", "termine", "archive"];
const GRAPHISME_PIPELINE = ["nouvelle_demande", "a_analyser", "infos_manquantes", "devis_a_creer", "devis_envoye", "attente_validation_devis", "devis_accepte", "devis_refuse", "acompte_a_regler", "a_planifier", "planifie_partiel", "planifie", "en_cours", "attente_elements_client", "pret_a_envoyer", "envoye_validation", "modifications_demandees", "en_modification", "partiellement_valide", "projet_valide", "a_facturer", "facture", "attente_reglement", "paye", "livre", "termine", "archive"];
const GRAPHISME_LABELS = { nouvelle_demande: "Nouvelle demande", a_analyser: "À analyser", infos_manquantes: "Infos manquantes", devis_a_creer: "Devis à créer", devis_envoye: "Devis envoyé", attente_validation_devis: "Devis en validation", devis_accepte: "Devis accepté", devis_refuse: "Devis refusé", acompte_a_regler: "Acompte à régler", a_planifier: "À planifier", planifie_partiel: "Planifié partiel", planifie: "Planifié", en_cours: "En cours", attente_elements_client: "Éléments client", pret_a_envoyer: "Prêt à envoyer", envoye_validation: "En review", modifications_demandees: "Modifications", en_modification: "En modification", partiellement_valide: "Partiellement validé", projet_valide: "Projet validé", a_facturer: "À facturer", facture: "Facturé", attente_reglement: "Attente règlement" };
const CLIENT_MOVES = ["validation", "modification", "comment", "contenu", "contenus", "demande", "devis"];
const REQUEST_FINAL = ["terminee", "annulee", "refusee"];

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const relWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = parseISO(iso);
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    if (mins < 60) return `il y a ${mins} min`;
    const h = Math.round(mins / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.round(h / 24)} j`;
  } catch { return ""; }
};
const deadlineInfo = (dl) => {
  if (!dl) return null;
  try {
    const d = parseISO(dl.slice(0, 10));
    if (isPast(d) && !isToday(d)) return { label: "En retard", cls: "text-[#BE123C]", urgent: 0 };
    if (isToday(d)) return { label: "Aujourd'hui", cls: "text-[#BE123C]", urgent: 1 };
    if (isTomorrow(d)) return { label: "Demain", cls: "text-[#B45309]", urgent: 2 };
    return { label: frDate(dl.slice(0, 10)), cls: "text-[#475569]", urgent: 3 };
  } catch { return null; }
};
const pubVisual = (p) => (p ? pubVisualFull(p).src : null);

/* Vignette image avec repli sur initiales colorées */
function Thumb({ src, name, color, testid, className = "w-9 h-9 rounded-lg" }) {
  const [err, setErr] = useState(false);
  if (src && !err) {
    return <img src={src} alt="" loading="lazy" onError={() => setErr(true)} data-testid={testid}
      className={`${className} object-cover shrink-0 border border-neutral-100`} />;
  }
  return (
    <span data-testid={testid} className={`${className} flex items-center justify-center text-[10px] font-bold text-white shrink-0`}
      style={{ background: color || "#94A3B8" }}>{initials(name)}</span>
  );
}

/* Icône couleur par type de document */
function docIcon(doc) {
  const ct = (doc.content_type || "").toLowerCase();
  const fn = (doc.file_name || doc.title || "").toLowerCase();
  if (ct.includes("pdf") || fn.endsWith(".pdf")) return { Icon: FileText, ...PASTEL.rose };
  if (ct.includes("sheet") || ct.includes("excel") || /\.(xlsx?|csv)$/.test(fn)) return { Icon: FileSpreadsheet, ...PASTEL.green };
  if (ct.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/.test(fn)) return { Icon: FileImage, ...PASTEL.blue };
  if (/\.(fig|sketch|ai|psd)$/.test(fn)) return { Icon: File, ...PASTEL.purple };
  return { Icon: FileText, ...PASTEL.blue };
}

/* ---------- Cartes KPI ---------- */
function KpiCard({ testid, icon: Icon, pastel, value, label, sub, to }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => to && navigate(to)} data-testid={testid}
      className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm px-4 py-3.5 text-left hover:border-neutral-300 hover:shadow transition-all group flex gap-3 items-center">
      <span className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0 transition-transform group-hover:scale-105"
        style={{ background: pastel.bg, color: pastel.text, borderColor: pastel.border }}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-[#475569] flex items-center justify-between gap-1">
          <span className="truncate">{label}</span>
          <ChevronRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-neutral-500 transition-colors shrink-0" />
        </p>
        <p className="font-heading text-2xl font-bold text-[#0F172A] leading-tight">{value}</p>
        {sub && <p className="text-[11px] truncate" style={{ color: sub.color || "#15803D" }} data-testid={`${testid}-sub`}>{sub.text}</p>}
      </div>
    </button>
  );
}

/* ---------- Carte + en-tête ---------- */
function CardHead({ icon: Icon, title, link, linkLabel, count }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100">
      <Icon className="w-4 h-4 text-[#0F172A]" strokeWidth={2} />
      <h3 className="font-heading font-bold text-sm text-[#0F172A]">{title}</h3>
      {count != null && <span className="text-[10px] text-neutral-400 bg-neutral-100 rounded-full px-1.5 py-0.5" data-testid={`section-count-${title}`}>{count}</span>}
      {link && (
        <Link to={link} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline flex items-center gap-0.5 shrink-0" data-testid={`${title.toLowerCase().replace(/[^a-z]+/g, "-")}-see-all`}>
          {linkLabel || "Voir tout"} <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}
const Card = ({ children, testid, className = "" }) => (
  <div className={`bg-white border border-[#E2E8F0] rounded-xl shadow-sm overflow-hidden flex flex-col ${className}`} data-testid={testid}>{children}</div>
);

/* ---------- Mes priorités du jour ---------- */
function PriorityRow({ item }) {
  const badge = SERVICE_BADGE[item.service];
  return (
    <div className={`flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 ${item.done ? "opacity-50" : ""}`} data-testid={`prio-${item.id}`}>
      {item.onCheck ? (
        <button onClick={item.onCheck} data-testid={`prio-check-${item.id}`}
          className="w-[18px] h-[18px] rounded-full border-2 border-neutral-300 hover:border-[#15803D] hover:bg-[#DCFCE7] transition-colors shrink-0" title="Marquer comme finalisé" />
      ) : (
        <span className={`w-[18px] h-[18px] rounded-full border-2 shrink-0 ${item.urgent <= 1 ? "border-[#FB7185] bg-[#FFE4E6]" : "border-neutral-200"}`} />
      )}
      <button onClick={item.onOpen} className="flex-1 min-w-0 text-left group" data-testid={`prio-open-${item.id}`}>
        <p className="text-[13px] font-medium text-[#0F172A] truncate group-hover:text-[#0369A1] transition-colors">{item.title}</p>
        {item.sub && <p className="text-[11px] text-neutral-400 truncate">{item.sub}</p>}
      </button>
      {badge && (
        <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border whitespace-nowrap hidden sm:inline"
          style={{ background: badge.bg, color: badge.text, borderColor: badge.border }}>{badge.label}</span>
      )}
      {item.deadline && <span className={`text-[11px] font-medium whitespace-nowrap ${item.deadline.cls}`}>{item.deadline.label}</span>}
      {item.action}
    </div>
  );
}

/* ---------- Mon agenda aujourd'hui ---------- */
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
function AgendaToday({ blocks, colorById, clientsById }) {
  const navigate = useNavigate();
  const today = format(new Date(), "yyyy-MM-dd");
  const items = (blocks || []).filter((b) => b.date === today).sort((a, b) => a.start_min - b.start_min).slice(0, 4);
  if (!blocks) return <p className="px-4 py-5 text-[13px] text-neutral-400">Chargement…</p>;
  if (items.length === 0) return (
    <div className="px-4 py-5" data-testid="agenda-today-empty">
      <p className="text-[13px] text-neutral-400 mb-2">Aucun créneau planifié aujourd'hui.</p>
      <Link to="/agenda?new=1" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0369A1] hover:underline" data-testid="agenda-today-add">
        <Plus className="w-3 h-3" /> Ajouter un créneau
      </Link>
    </div>
  );
  return (
    <div className="px-4 py-2" data-testid="agenda-today">
      {items.map((b, i) => {
        const color = (b.client_id && colorById[b.client_id]) || b.client?.color || "#94A3B8";
        const clientName = (typeof b.client === "string" ? b.client : b.client?.name) || (b.client_id && clientsById[b.client_id]?.name) || "";
        const logo = b.client_id && clientsById[b.client_id]?.brand?.logo_url;
        return (
          <button key={b.id || i} onClick={() => navigate(`/agenda?week=${today}&highlight=${b.id}`)}
            className="w-full flex gap-2.5 relative text-left group" data-testid={`agenda-today-block-${b.id || i}`}>
            {i < items.length - 1 && <span className="absolute left-[3px] top-5 bottom-0 w-px bg-neutral-200" />}
            <span className="w-[7px] h-[7px] rounded-full mt-2 shrink-0" style={{ background: color }} />
            <div className="text-[10px] font-semibold text-[#475569] leading-tight pt-0.5 w-8 shrink-0">
              <p>{fmtMin(b.start_min)}</p><p className="text-neutral-300 font-normal">{fmtMin(b.end_min)}</p>
            </div>
            <div className="flex-1 min-w-0 pb-3 pl-2.5 border-l-2" style={{ borderColor: color }}>
              <p className="text-[13px] font-medium text-[#0F172A] truncate group-hover:text-[#0369A1] transition-colors">{b.title}</p>
              {clientName && <p className="text-[11px] text-neutral-400 truncate">{clientName}</p>}
            </div>
            {clientName && <Thumb src={logo} name={clientName} color={color} className="w-7 h-7 rounded-full mt-0.5" />}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Projets en cours par service ---------- */
function ProjectsByService({ clients, pubs, gprojs, wprojs, visualByClient }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("tous");
  const month = format(new Date(), "yyyy-MM");
  const cmLines = useMemo(() => clients.filter((c) => (c.objective_month || 0) > 0).map((c) => {
    const done = pubs.filter((p) => p.client_id === c.id && (p.date || "").startsWith(month) && ["valide", "programme", "publie"].includes(p.status)).length;
    return { ...c, done, pct: Math.min(100, Math.round((done / c.objective_month) * 100)) };
  }), [clients, pubs, month]);
  const gLines = gprojs.filter((p) => !GRAPHISME_FINAL.includes(p.status));
  const wLines = wprojs.filter((p) => !["livre", "termine", "archive"].includes(p.stage || p.op_status));
  const counts = { tous: cmLines.length + gLines.length + wLines.length, cm: cmLines.length, graphisme: gLines.length, web: wLines.length };
  const tabs = [["tous", "Tous"], ["cm", "Community Management"], ["graphisme", "Graphisme"], ["web", "Web Design"]];
  const clientLogo = (cid) => clients.find((c) => c.id === cid)?.brand?.logo_url;

  const Progress = ({ pct, color = "#0B132B" }) => (
    <div className="h-1 w-16 bg-neutral-100 rounded-full overflow-hidden shrink-0 hidden sm:block">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
  const SlotBtn = ({ clientId }) => (
    <button onClick={(e) => { e.stopPropagation(); navigate(`/agenda?new=1&client=${clientId}`); }}
      className="text-[10px] font-semibold text-[#0369A1] border border-[#BAE6FD] bg-[#E0F2FE] rounded-full px-2 py-0.5 hover:bg-[#BAE6FD] transition-colors whitespace-nowrap shrink-0"
      data-testid={`project-slot-${clientId}`} title="Ajouter un créneau à l'agenda">+ Créneau</button>
  );

  const rows = [];
  if (tab === "tous" || tab === "cm") cmLines.forEach((c) => rows.push({ kind: "cm", data: c }));
  if (tab === "tous" || tab === "graphisme") gLines.forEach((p) => rows.push({ kind: "g", data: p }));
  if (tab === "tous" || tab === "web") wLines.forEach((p) => rows.push({ kind: "w", data: p }));
  const shown = rows.slice(0, 3);

  return (
    <div className="flex flex-col flex-1">
      <div className="flex gap-1 px-4 pt-1.5 pb-2 flex-wrap" data-testid="projects-tabs">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`projects-tab-${id}`}
            className={`text-[11px] font-medium rounded-full px-2.5 py-1 transition-colors ${tab === id ? "bg-[#0B132B] text-white" : "text-[#475569] hover:bg-neutral-100"}`}>
            {label} <span className={tab === id ? "text-neutral-400" : "text-neutral-300"}>{counts[id]}</span>
          </button>
        ))}
      </div>
      <div>
        {rows.length === 0 && <p className="px-4 py-5 text-[13px] text-neutral-400">Aucun projet en cours pour ce service.</p>}
        {shown.map(({ kind, data }) => {
          if (kind === "cm") {
            const c = data;
            return (
              <div key={`cm-${c.id}`} className="flex items-center gap-3 px-4 py-2.5 border-t border-neutral-100 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => navigate(`/clients/${c.id}`)} data-testid={`project-cm-${c.id}`}>
                <Thumb src={visualByClient[c.id] || c.brand?.logo_url} name={c.name} color={c.color} testid={`project-cm-thumb-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#0F172A] truncate">{c.name}</p>
                  <p className="text-[11px] text-neutral-400 truncate">Réseaux sociaux · objectif {c.objective_month}/mois</p>
                </div>
                <span className="text-[11px] font-semibold text-[#475569] whitespace-nowrap" data-testid={`project-cm-pct-${c.id}`}>{c.done}/{c.objective_month} · {c.pct} %</span>
                <Progress pct={c.pct} color={c.pct >= 100 ? "#4ADE80" : "#0B132B"} />
                <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border whitespace-nowrap hidden md:inline"
                  style={c.pct >= 100
                    ? { background: PASTEL.green.bg, color: PASTEL.green.text, borderColor: PASTEL.green.border }
                    : { background: PASTEL.blue.bg, color: PASTEL.blue.text, borderColor: PASTEL.blue.border }}>
                  {c.pct >= 100 ? "Atteint" : "En cours"}
                </span>
                <SlotBtn clientId={c.id} />
              </div>
            );
          }
          if (kind === "g") {
            const p = data;
            const pct = Math.round((GRAPHISME_PIPELINE.indexOf(p.status) / (GRAPHISME_PIPELINE.length - 1)) * 100);
            const review = ["envoye_validation", "attente_validation_devis", "partiellement_valide"].includes(p.status);
            const badge = review ? PASTEL.amber : PASTEL.blue;
            return (
              <div key={`g-${p.id}`} className="flex items-center gap-3 px-4 py-2.5 border-t border-neutral-100 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => navigate("/graphisme")} data-testid={`project-g-${p.id}`}>
                <Thumb src={clientLogo(p.client_id)} name={p.client_name} color="#B45309" testid={`project-g-thumb-${p.id}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#0F172A] truncate">{p.name}</p>
                  <p className="text-[11px] text-neutral-400 truncate">{p.client_name}{p.deadline ? ` · ${frDate(p.deadline.slice(0, 10))}` : ""}</p>
                </div>
                <span className="text-[11px] font-semibold text-[#475569]">{pct} %</span>
                <Progress pct={pct} color="#F59E0B" />
                <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border whitespace-nowrap hidden md:inline"
                  style={{ background: badge.bg, color: badge.text, borderColor: badge.border }}>
                  {GRAPHISME_LABELS[p.status] || p.status}
                </span>
                <SlotBtn clientId={p.client_id} />
              </div>
            );
          }
          const p = data;
          return (
            <div key={`w-${p.id}`} className="flex items-center gap-3 px-4 py-2.5 border-t border-neutral-100 cursor-pointer hover:bg-neutral-50 transition-colors"
              onClick={() => navigate("/sites-web")} data-testid={`project-w-${p.id}`}>
              <Thumb src={clientLogo(p.client_id)} name={p.client_name} color="#6B21A8" testid={`project-w-thumb-${p.id}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#0F172A] truncate">{p.name}</p>
                <p className="text-[11px] text-neutral-400 truncate">{p.client_name}</p>
              </div>
              <span className="text-[11px] font-semibold text-[#475569]">{p.progress || 0} %</span>
              <Progress pct={p.progress || 0} color="#8B5CF6" />
              <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border whitespace-nowrap hidden md:inline"
                style={{ background: PASTEL.purple.bg, color: PASTEL.purple.text, borderColor: PASTEL.purple.border }}>En cours</span>
              <SlotBtn clientId={p.client_id} />
            </div>
          );
        })}
        {rows.length > shown.length && (
          <Link to={tab === "graphisme" ? "/graphisme" : tab === "web" ? "/sites-web" : "/clients"}
            className="block px-4 py-2 text-[11px] font-semibold text-[#0369A1] hover:bg-neutral-50 border-t border-neutral-100 transition-colors"
            data-testid="projects-see-all">
            Voir tout ({rows.length}) →
          </Link>
        )}
      </div>
    </div>
  );
}

/* ---------- Demandes clients récentes ---------- */
function RecentRequests({ items, clientById }) {
  const navigate = useNavigate();
  return (
    <div data-testid="demands-recent">
      {items.length === 0 && <p className="px-4 py-5 text-[13px] text-neutral-400">Aucune demande en cours.</p>}
      {items.map((r) => {
        const st = clientRequestStatus(r.status);
        const cl = clientById[r.client_id];
        return (
          <button key={r.id} onClick={() => navigate(`/demandes?focus=${r.id}`)} data-testid={`demand-${r.id}`}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
            <Thumb src={cl?.brand?.logo_url} name={r.client_name} color={cl?.color} testid={`demand-thumb-${r.id}`} />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-[#0F172A] truncate">{r.title}</p>
              <p className="text-[11px] text-neutral-400 truncate">{r.client_name} · {relWhen(r.created_at)}</p>
            </div>
            {r.urgency === "urgent" && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border" style={{ background: PASTEL.rose.bg, color: PASTEL.rose.text, borderColor: PASTEL.rose.border }}>Urgent</span>}
            <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`demand-status-${r.id}`}>{st.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Rentabilité du mois ---------- */
function MonthProfitability({ acc }) {
  const months = (acc?.months || []).slice(-7);
  const max = Math.max(1, ...months.map((m) => Math.max(m.encaisse || 0, m.facture || 0)));
  const enc = acc?.encaisse_mois || 0, fac = acc?.facture_mois || 0, obj = acc?.objectif_mensuel || 0;
  const reste = Math.max(0, obj - enc);
  return (
    <div className="px-4 py-3 flex-1 flex flex-col" data-testid="rentability-month">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="font-heading text-2xl font-bold text-[#0F172A]" data-testid="rentability-encaisse">{fmtEuro(enc)}</p>
          <p className="text-[11px] text-neutral-400">encaissés en {format(new Date(), "MMMM", { locale: fr })}</p>
        </div>
        <div className="flex items-end gap-1 h-14 flex-1 max-w-40 justify-end">
          {months.map((m) => (
            <div key={m.key} className="flex-1 rounded-t" title={`${m.label} — ${fmtEuro(m.encaisse)}`}
              style={{ height: `${Math.max(8, ((m.encaisse || 0) / max) * 52)}px`, background: m.key === format(new Date(), "yyyy-MM") ? "#0B132B" : "#E2E8F0" }} />
          ))}
        </div>
      </div>
      <div className="mt-3 space-y-1 border-t border-neutral-100 pt-2.5 text-[11px]">
        {[["Encaissé", enc, "#0B132B"], ["Facturé", fac, "#94A3B8"], ["Objectif du mois", obj, "#F59E0B"]].map(([label, val, color]) => (
          <div key={label} className="flex items-center gap-2" data-testid={`rentability-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
            <span className="text-[#475569] flex-1">{label}</span>
            <span className="font-semibold text-[#0F172A]">{fmtEuro(val)}</span>
          </div>
        ))}
        {obj > 0 && (
          <div className="flex items-center gap-2" data-testid="rentability-reste">
            <span className="w-2 h-2 rounded-sm bg-[#DCFCE7]" />
            <span className="text-[#475569] flex-1">Reste à atteindre</span>
            <span className="font-semibold" style={{ color: reste > 0 ? "#B45309" : "#15803D" }}>{reste > 0 ? fmtEuro(reste) : "Atteint"}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Dernières activités (mouvements client) ---------- */
function RecentActivity({ notifs, pubById, clientById }) {
  const navigate = useNavigate();
  const items = useMemo(() => {
    const moves = notifs.filter((n) => CLIENT_MOVES.includes(n.type));
    const groups = new Map();
    const out = [];
    moves.forEach((n) => {
      const pub = n.publication_id && pubById[n.publication_id];
      const cid = pub?.client_id;
      const day = (n.created_at || "").slice(0, 10);
      if (cid && ["validation", "modification"].includes(n.type)) {
        const key = `${cid}-${n.type}-${day}`;
        if (groups.has(key)) { groups.get(key).count += 1; return; }
        const g = { id: n.id, client: clientById[cid], type: n.type, count: 1, created_at: n.created_at, url: n.url };
        groups.set(key, g); out.push(g); return;
      }
      out.push({ id: n.id, text: n.text, type: n.type, created_at: n.created_at, url: n.url });
    });
    return out.slice(0, 4);
  }, [notifs, pubById, clientById]);

  return (
    <div data-testid="activity-recent">
      {items.length === 0 && <p className="px-4 py-5 text-[13px] text-neutral-400">Aucun mouvement client récent.</p>}
      {items.map((a) => {
        const client = a.client;
        const label = a.text || (a.type === "validation"
          ? `${client?.name || "Un client"} a validé ${a.count} publication${a.count > 1 ? "s" : ""}`
          : `${client?.name || "Un client"} a demandé des modifications sur ${a.count} publication${a.count > 1 ? "s" : ""}`);
        return (
          <button key={a.id} onClick={() => navigate(a.url || "/notifications")} data-testid={`activity-item-${a.id}`}
            className="w-full flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
            <Thumb src={client?.brand?.logo_url} name={client?.name || "DGA"} color={client?.color} className="w-7 h-7 rounded-full" testid={`activity-thumb-${a.id}`} />
            <p className="flex-1 min-w-0 text-[13px] text-[#0F172A] truncate">{label}</p>
            {client && <span className="text-[10px] text-neutral-400 border border-neutral-200 rounded-full px-1.5 py-0.5 whitespace-nowrap hidden lg:inline">{client.name}</span>}
            <span className="text-[10px] text-neutral-400 whitespace-nowrap">{relWhen(a.created_at)}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Documents récents ---------- */
function RecentDocuments({ docs }) {
  const navigate = useNavigate();
  const shown = docs.slice(0, 4);
  return (
    <div data-testid="documents-recent">
      {shown.length === 0 && <p className="px-4 py-5 text-[13px] text-neutral-400">Aucun document récent.</p>}
      {shown.map((d) => {
        const { Icon, bg, text, border } = docIcon(d);
        return (
          <button key={d.id} onClick={() => navigate("/graphisme/documents")} data-testid={`document-${d.id}`}
            className="w-full flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center border shrink-0" style={{ background: bg, color: text, borderColor: border }}>
              <Icon className="w-3.5 h-3.5" />
            </span>
            <p className="flex-1 min-w-0 text-[12px] font-medium text-[#0F172A] truncate">{d.title || d.file_name || "Document"}</p>
            <span className="text-[10px] text-neutral-400 whitespace-nowrap">{relWhen(d.created_at)}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ================= Dashboard ================= */
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [clients, setClients] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [rdvToday, setRdvToday] = useState([]);
  const [rdvAll, setRdvAll] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [acc, setAcc] = useState(null);
  const [gprojs, setGprojs] = useState([]);
  const [wprojs, setWprojs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showArchives, setShowArchives] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const loadDash = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { loadDash(); }, []);
  useEffect(() => { api.get("/agenda").then((r) => setAgenda(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/publications").then((r) => setPubs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    api.get("/appointments").then((r) => {
      const list = r.data || [];
      setRdvAll(list);
      setRdvToday(list.filter((a) => a.date === today));
    }).catch(() => {});
  }, []);
  useEffect(() => { api.get("/notifications").then((r) => setNotifs((r.data || []).filter((n) => !n.archived))).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/client-requests").then((r) => {
      const open = (r.data || []).filter((x) => !REQUEST_FINAL.includes(x.status));
      open.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRequests(open);
    }).catch(() => {});
  }, []);
  useEffect(() => { api.get("/accounting/dashboard").then((r) => setAcc(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/graphisme/projects").then((r) => setGprojs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/web/projects").then((r) => setWprojs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/graphisme/documents").then((r) => setDocs(r.data || [])).catch(() => {}); }, []);

  const unarchiveTask = async (t) => { await api.patch(`/tasks/${t.id}`, { archived: false }); toast.success("Tâche restaurée"); loadDash(); };
  const deleteTask = async (t) => { await api.delete(`/tasks/${t.id}`); toast.success("Tâche supprimée définitivement"); loadDash(); };
  const completeTask = async (t) => { await api.patch(`/tasks/${t.id}`, { status: "finalise" }); toast.success("Tâche finalisée"); loadDash(); };

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const pubById = useMemo(() => Object.fromEntries(pubs.map((p) => [p.id, p])), [pubs]);
  const colorById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.color])), [clients]);
  const visualByClient = useMemo(() => {
    const map = {};
    [...pubs].sort((a, b) => (b.date || "").localeCompare(a.date || "")).forEach((p) => {
      if (!map[p.client_id]) { const v = pubVisual(p); if (v) map[p.client_id] = v; }
    });
    return map;
  }, [pubs]);

  const { stats } = data || {};
  if (!data) {
    return <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const archivedTasks = data.tasks.filter((t) => t.archived);
  const month = format(new Date(), "yyyy-MM");

  /* --- Priorités du jour --- */
  const priorities = [];
  const alertByClient = {};
  data.today.filter((p) => !["programme", "publie"].includes(p.status)).forEach((p) => {
    (alertByClient[p.client_id] = alertByClient[p.client_id] || { client_id: p.client_id, client_name: p.client_name, pending: 0, total: 0 });
    alertByClient[p.client_id].total += 1;
    if (p.status === "a_valider") alertByClient[p.client_id].pending += 1;
  });
  Object.values(alertByClient).forEach((a) => {
    priorities.push({
      id: `alert-${a.client_id}`, urgent: a.pending > 0 ? 0 : 1, service: "cm",
      title: a.pending > 0
        ? `${a.pending} publication${a.pending > 1 ? "s" : ""} en attente de validation, à publier aujourd'hui`
        : `${a.total} publication${a.total > 1 ? "s" : ""} validée${a.total > 1 ? "s" : ""} non programmée${a.total > 1 ? "s" : ""} pour aujourd'hui`,
      sub: a.client_name,
      deadline: { label: "Aujourd'hui", cls: "text-[#BE123C]" },
      onOpen: () => navigate(`/clients/${a.client_id}`),
      action: a.pending > 0 ? <NotifyClientButton clientId={a.client_id} pendingCount={a.pending} /> : null,
    });
  });
  data.tasks.filter((t) => !t.archived && !["finalise", "livre_client"].includes(t.status) && t.deadline).forEach((t) => {
    const d = deadlineInfo(t.deadline);
    priorities.push({
      id: `task-${t.id}`, urgent: d?.urgent ?? 3, service: t.client_id ? "client" : null,
      title: t.title, sub: t.client_id ? clientById[t.client_id]?.name : "Tâche interne",
      deadline: d, onOpen: () => navigate("/taches"), onCheck: () => completeTask(t),
    });
  });
  const soon = format(new Date(Date.now() + 3 * 86400000), "yyyy-MM-dd");
  gprojs.filter((p) => !GRAPHISME_FINAL.includes(p.status) && (["urgent", "prioritaire"].includes(p.priority) || (p.deadline && p.deadline.slice(0, 10) <= soon)))
    .forEach((p) => {
      const d = deadlineInfo(p.deadline);
      priorities.push({
        id: `gproj-${p.id}`, urgent: p.priority === "urgent" ? 0 : (d?.urgent ?? 2), service: "graphisme",
        title: p.name, sub: `${p.client_name}${p.priority !== "normal" ? ` · ${p.priority}` : ""}`,
        deadline: d, onOpen: () => navigate("/graphisme"),
      });
    });
  const todayIso = format(new Date(), "yyyy-MM-dd");
  const pendingReports = rdvAll.filter((r) =>
    r.date < todayIso && r.booking_status !== "annule" && r.rdv_status !== "annule" && !r.completed &&
    !["finalise", "envoye"].includes(r.report_status || "non_commence"));
  if (pendingReports.length > 0) {
    priorities.push({
      id: "pending-reports", urgent: 1, service: "cm",
      title: `${pendingReports.length} compte${pendingReports.length > 1 ? "s" : ""} rendu${pendingReports.length > 1 ? "s" : ""} en attente de rédaction`,
      sub: "Rendez-vous passés sans compte rendu finalisé",
      deadline: { label: "À rédiger", cls: "text-[#B45309]" },
      onOpen: () => navigate("/rdv"),
    });
  }
  priorities.sort((a, b) => a.urgent - b.urgent);
  const prioritiesShown = priorities.slice(0, 4);

  const newClientsMonth = clients.filter((c) => (c.created_at || "").startsWith(month)).length;
  const activeGprojs = gprojs.filter((p) => !GRAPHISME_FINAL.includes(p.status)).length;
  const activeWprojs = wprojs.filter((p) => !["livre", "termine", "archive"].includes(p.stage || p.op_status)).length;
  const pendingClients = new Set(data.pending_validation.map((p) => p.client_id)).size;

  return (
    <div className="space-y-4" data-testid="dashboard">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" data-testid="dashboard-greeting">Bonjour {user?.name}</h1>
          <p className="text-xs text-neutral-500 mt-0.5 capitalize">{format(new Date(), "EEEE d MMMM yyyy", { locale: fr })} — voici les éléments à traiter aujourd'hui.</p>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-[#64748B] italic text-sm leading-snug text-right hidden md:block" data-testid="dashboard-quote">« Des idées fortes,<br />pour des marques qui comptent. »</p>
          <button data-testid="booking-link-btn" onClick={() => setShowBooking(true)}
            className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-3 py-2 text-[11px] font-semibold hover:bg-[#1C2A4F] transition-colors">
            <Link2 className="w-3.5 h-3.5" /> Lien de RDV
          </button>
        </div>
      </div>
      {showBooking && <BookingLinkModal onClose={() => setShowBooking(false)} />}

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiCard testid="kpi-clients-actifs" icon={Users} pastel={PASTEL.blue} value={clients.filter((c) => !c.archived).length} label="Clients actifs"
          sub={newClientsMonth > 0 ? { text: `+${newClientsMonth} ce mois` } : { text: "à jour" }} to="/clients" />
        <KpiCard testid="kpi-projets" icon={FolderKanban} pastel={PASTEL.amber} value={activeGprojs + activeWprojs} label="Projets en cours"
          sub={{ text: `${activeGprojs} graphisme · ${activeWprojs} web`, color: "#B45309" }} to="/graphisme" />
        <KpiCard testid="kpi-en-validation" icon={Eye} pastel={PASTEL.rose} value={stats.pending} label="Éléments à valider"
          sub={{ text: `${pendingClients} client${pendingClients > 1 ? "s" : ""} concerné${pendingClients > 1 ? "s" : ""}`, color: "#BE123C" }} to="/contenus" />
        <KpiCard testid="kpi-rdv-jour" icon={CalendarCheck} pastel={PASTEL.green} value={rdvToday.length} label="Rendez-vous du jour"
          sub={{ text: rdvToday[0]?.start_time ? `prochain à ${rdvToday[0].start_time}` : "aujourd'hui" }} to="/rdv" />
      </div>

      {/* Priorités + Agenda */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card testid="priorities-card" className="lg:col-span-3">
          <CardHead icon={Zap} title="Mes priorités du jour" link="/taches" linkLabel="Voir toutes" count={priorities.length} />
          <div>
            {priorities.length === 0 && <p className="px-4 py-5 text-[13px] text-neutral-400" data-testid="priorities-empty">Rien d'urgent aujourd'hui — belle journée !</p>}
            {prioritiesShown.map((p) => <PriorityRow key={p.id} item={p} />)}
            {priorities.length > prioritiesShown.length && (
              <Link to="/taches" className="block px-4 py-2 text-[11px] font-semibold text-[#0369A1] hover:bg-neutral-50 transition-colors" data-testid="priorities-more">
                Voir tout ({priorities.length}) →
              </Link>
            )}
          </div>
        </Card>
        <Card testid="agenda-today-card" className="lg:col-span-2">
          <CardHead icon={CalendarCheck} title="Mon agenda aujourd'hui" link="/agenda" linkLabel="Voir l'agenda" />
          <AgendaToday blocks={agenda?.blocks} colorById={colorById} clientsById={clientById} />
        </Card>
      </div>

      {/* Projets par service + Demandes */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card testid="projects-service-card" className="lg:col-span-3">
          <CardHead icon={FolderKanban} title="Projets en cours par service" link="/clients" linkLabel="Voir tous les projets" />
          <ProjectsByService clients={clients.filter((c) => !c.archived)} pubs={pubs} gprojs={gprojs} wprojs={wprojs} visualByClient={visualByClient} />
        </Card>
        <Card testid="demands-card" className="lg:col-span-2">
          <CardHead icon={Inbox} title="Demandes clients récentes" link="/demandes" linkLabel="Voir toutes" count={requests.length} />
          <RecentRequests items={requests.slice(0, 4)} clientById={clientById} />
        </Card>
      </div>

      {/* Rentabilité + Activités + Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="rentability-card">
          <CardHead icon={BarChart3} title="Rentabilité du mois" link="/comptabilite" linkLabel="Comptabilité" />
          <MonthProfitability acc={acc} />
        </Card>
        <Card testid="activity-card">
          <CardHead icon={Bell} title="Dernières activités" link="/notifications" linkLabel="Voir toutes" />
          <RecentActivity notifs={notifs} pubById={pubById} clientById={clientById} />
        </Card>
        <Card testid="documents-card">
          <CardHead icon={FileText} title="Documents récents" link="/graphisme/documents" linkLabel="Voir tous" />
          <RecentDocuments docs={docs} />
        </Card>
      </div>

      {/* Archives des tâches (seul point de restauration) */}
      {archivedTasks.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm overflow-hidden" data-testid="dash-archives-section">
          <button data-testid="dash-archives-toggle" onClick={() => setShowArchives(!showArchives)}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-neutral-50 transition-colors">
            <ChevronRight className={`w-3.5 h-3.5 text-neutral-400 transition-transform ${showArchives ? "rotate-90" : ""}`} />
            <Archive className="w-3.5 h-3.5 text-neutral-400" />
            <span className="font-heading font-bold text-[13px] text-[#0F172A]">Archives</span>
            <span className="ml-auto text-[10px] text-neutral-400 bg-neutral-100 rounded-full px-1.5 py-0.5" data-testid="dash-archives-count">{archivedTasks.length}</span>
          </button>
          {showArchives && archivedTasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-2 border-t border-neutral-100" data-testid={`dash-archived-task-${t.id}`}>
              <p className="text-[13px] flex-1 truncate text-neutral-500">{t.title}</p>
              <span className="text-[11px] text-neutral-400 whitespace-nowrap">
                {t.estimate_min ? `~${t.estimate_min} min` : ""}
                {(t.time_spent_sec || 0) > 0 ? `${t.estimate_min ? " · " : ""}⏱ ${Math.floor(t.time_spent_sec / 60)} min` : ""}
              </span>
              <button data-testid={`dash-task-unarchive-${t.id}`} onClick={() => unarchiveTask(t)}
                className="text-neutral-400 hover:text-neutral-900 transition-colors" title="Restaurer">
                <ArchiveRestore className="w-4 h-4" />
              </button>
              <button data-testid={`dash-task-delete-${t.id}`} onClick={() => deleteTask(t)}
                className="text-neutral-300 hover:text-red-500 transition-colors" title="Supprimer définitivement">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
